import type { PeriodSelectionOption } from './types'
import {
  dateTypes,
  periodSelectionTypes,
  periodSelectionCategories,
} from './types'

export const comparisonModes = {
  IS: 'is',
  IS_NOT: 'is not',
  PARTIALLY_STARRED: 'partially_starred',
  UP_TO_STATUS: 'up to status',
  FROM_STATUS: 'from status',
  EQUAL: 'equal',
  NOT_EQUAL: 'not_equal',
  EXACTLY: 'exactly',
  SHOW_ALL: 'show-all',
  CONTAINS: 'contains',
  LESS_THAN: 'less_than',
  GREATER_THAN: 'greater_than',
  LESS_THAN_OR_EQUAL: 'less_than_or_equal',
  GREATER_THAN_OR_EQUAL: 'greater_than_or_equal',
  CURRENT_USER: 'current-user',
  USER: 'user',
  TRUE: 'true',
  FALSE: 'false',
}

export const InsightsStaticColumns = {
  NAME: 'name',
  SELECTED_PERIOD: 'selectedPeriod',
} as const

export const filters = {
  BOOKYEAR_END: 'BookyearEnd',
  COMPANY_DATA: 'CompanyData',
  COMPANY_GROUPS: 'CompanyGroups',
  FOLLOW_COMPANIES: 'FollowCompanies',
  FORMULA: 'Formula',
  PERIOD_LOCKED: 'PeriodLocked',
  PREPARATION_AND_REVIEW: 'PreparationAndReview',
  RECONCILIATION_RESULT: 'ReconciliationResult',
  SUBMISSION_DATA: 'SubmissionData',
  SYNCS_INFORMATION: 'SyncsInformation',
  TEMPLATE_STARRING: 'TemplateStarring',
  TODOS: 'Todos',
  WORKFLOW_PROGRESS: 'WorkflowProgress',
  WORKFLOW_SHARING_STATUS: 'WorkflowSharingStatus',
  WORKFLOW_STATUS: 'WorkflowStatus',
  WORKFLOW_USAGE: 'WorkflowUsage',
} as const

export const RIGHT_ALIGN_FILTER_TYPES = [
  filters.FORMULA,
  filters.RECONCILIATION_RESULT,
]

export const SLOW_FILTERS = [
  filters.WORKFLOW_PROGRESS,
  filters.FORMULA,
  filters.RECONCILIATION_RESULT,
]

export const dateTypeOptions = [
  {
    key: dateTypes.TODAY,
    titleKey: 'companies_overview.period_selection_new.today_option',
  },
  {
    key: dateTypes.FIXED_DATE,
    titleKey: 'companies_overview.period_selection_new.fixed_date',
  },
]
export const periodSelectionOptions = [
  {
    key: periodSelectionTypes.FINANCIAL_YEAR,
    category: periodSelectionCategories.BY_END_DATE,
    titleKey: 'companies_overview.period_selection_new.financial_year_title',
    subtitleKey: null,
    icon: 'icon-period-selection-financial-year',
    illustration: 'illustration-period-selection-financial-year',
    tooltipKey:
      'companies_overview.period_selection_new.financial_year_tooltip',
  },
  {
    key: periodSelectionTypes.ACCOUNTING_PERIOD,
    category: periodSelectionCategories.BY_END_DATE,
    titleKey: 'companies_overview.period_selection_new.accounting_period_title',
    subtitle: null,
    icon: 'icon-period-selection-accounting-period',
    illustration: 'illustration-period-selection-accounting-period',
    tooltipKey:
      'companies_overview.period_selection_new.accounting_period_tooltip',
  },
  {
    key: periodSelectionTypes.FINANCIAL_YEAR_EXACT_DATE,
    category: periodSelectionCategories.BY_END_DATE,
    titleKey:
      'companies_overview.period_selection_new.financial_year_exact_date_title',
    subtitleKey: null,
    icon: 'icon-period-selection-financial-year-exact-date',
    illustration: 'illustration-period-selection-financial-year-exact-date',
    tooltipKey:
      'companies_overview.period_selection_new.financial_year_exact_date_tooltip',
    exactDate: true,
  },
  {
    key: periodSelectionTypes.ACTIVE_PERIOD,
    category: periodSelectionCategories.BY_LOCKING_STATE,
    titleKey: 'companies_overview.period_selection_new.active_period_title',
    subtitleKey:
      'companies_overview.period_selection_new.active_period_subtitle',
    icon: 'icon-period-selection-recent-unlocked',
    illustration: 'illustration-period-selection-recent-unlocked',
    tooltipKey: 'companies_overview.period_selection_new.active_period_tooltip',
  },
  {
    key: periodSelectionTypes.COMPLETED_PERIOD,
    category: periodSelectionCategories.BY_LOCKING_STATE,
    titleKey: 'companies_overview.period_selection_new.completed_period_title',
    subtitleKey:
      'companies_overview.period_selection_new.completed_period_subtitle',
    icon: 'icon-period-selection-recent-locked',
    illustration: 'illustration-period-selection-recent-locked',
    tooltipKey:
      'companies_overview.period_selection_new.completed_period_tooltip',
  },
] as Array<PeriodSelectionOption>

export const PrepareAndReviewStates = {
  preparation: 'preparation',
  review: 'review',
} as const

export const PrepareAndReviewScopes = {
  template: 'template',
  workflow: 'workflow',
} as const

export const TemplateStarringTargets = {
  reconciliations: 'reconciliations',
  accounts: 'accounts',
} as const

export type TemplateStarringTarget =
  typeof TemplateStarringTargets[keyof typeof TemplateStarringTargets]

export const SyncsInformationCategories = {
  bookkeeping: 'bookkeeping',
  administration: 'administration',
} as const

export type SyncsInformationCategory =
  typeof SyncsInformationCategories[keyof typeof SyncsInformationCategories]

export const SyncsInformationCategoryOptions = [
  {
    value: SyncsInformationCategories.bookkeeping,
    labelKey:
      'companies_overview.filters.syncs_information.categories.bookkeeping',
  },
  {
    value: SyncsInformationCategories.administration,
    labelKey:
      'companies_overview.filters.syncs_information.categories.administration',
  },
] as const

export const SyncsInformationScopes = {
  sync_type: 'sync_type',
  sync_name: 'sync_name',
  sync_reference: 'sync_reference',
  sync_status: 'sync_status',
} as const

export const SyncsInformationScopeOptions = [
  {
    value: SyncsInformationScopes.sync_type,
    labelKey: 'companies_overview.filters.syncs_information.scopes.sync_type',
  },
  {
    value: SyncsInformationScopes.sync_name,
    labelKey: 'companies_overview.filters.syncs_information.scopes.sync_name',
  },
  {
    value: SyncsInformationScopes.sync_reference,
    labelKey:
      'companies_overview.filters.syncs_information.scopes.sync_reference',
    descriptionKey:
      'companies_overview.filters.syncs_information.scopes.sync_reference_description',
  },
  {
    value: SyncsInformationScopes.sync_status,
    labelKey: 'companies_overview.filters.syncs_information.scopes.sync_status',
    descriptionKey:
      'companies_overview.filters.syncs_information.scopes.sync_status_description',
  },
] as const

export const SyncStatusValues = {
  SYNCHRONISED: 'synchronised',
  FAILED: 'failed',
  CANCELED: 'canceled',
  NOT_SYNCHRONISED: 'not_synchronised',
  IN_PROGRESS: 'in_progress',
  NO_SYNC_DATA: 'no_sync_data',
} as const

export type SyncStatusValue =
  typeof SyncStatusValues[keyof typeof SyncStatusValues]

export const WorkflowPreparationLabels = {
  PREPARED: 'prepared',
  PARTIALLY_PREPARED: 'partially_prepared',
  NOT_STARTED: 'preparation_not_started',
} as const

export const WorkflowReviewLabels = {
  REVIEWED: 'reviewed',
  PARTIALLY_REVIEWED: 'partially_reviewed',
  NOT_STARTED: 'review_not_started',
} as const

// @vueform/multiselect treats null as "no selection", so we use a token string
// as the option value for "Default Workflow". The backend convention is nil for
// the default workflow; conversion happens in serializeSettings/deserializeSettings.
export const DEFAULT_WORKFLOW_ID = 'default' as const

// The "no status" option of the workflow status filter. It is not a
// WorkflowStatus id. The backend holds the same value in
// WorkflowStatusFilterSettings::NO_STATUS_ID.
export const NO_WORKFLOW_STATUS_ID = -1

export const templateDropdownValues = {
  [PrepareAndReviewStates.preparation]: ['prepared'],
  [PrepareAndReviewStates.review]: ['previously_reviewed', 'reviewed'],
} as const

export const workflowDropdownValues = {
  [PrepareAndReviewStates.preparation]: [
    WorkflowPreparationLabels.PREPARED,
    WorkflowPreparationLabels.PARTIALLY_PREPARED,
    WorkflowPreparationLabels.NOT_STARTED,
  ],
  [PrepareAndReviewStates.review]: [
    WorkflowReviewLabels.REVIEWED,
    WorkflowReviewLabels.PARTIALLY_REVIEWED,
    WorkflowReviewLabels.NOT_STARTED,
  ],
} as const
